import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, NgZone, OnInit } from '@angular/core';
import { Collections, HomeTabs, Search } from '@local/client-contracts';
import { PopupService } from '@local/ui-infra';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ContextMenuService } from '@shared/components';
import { EventsService } from '@shared/services';
import { CustomKeyboardEvent, KeyboardService } from '@shared/services/keyboard.service';
import { RouterService } from '@shared/services/router.service';
import { SessionService } from '@shared/services/session.service';
import { isEnterKey, KeyName } from '@local/ts-infra';
import { WorkspacesService } from 'src/app/bar/services';
import { CollectionsService } from 'src/app/bar/services/collections.service';
import { ResultCommandService } from 'src/app/bar/services/commands/result-command.service';
import { HubService } from 'src/app/bar/services/hub.service';
import { SearchOptions, SearchService } from 'src/app/bar/services/search';
import { FavoritesSourceSettings } from 'src/app/bar/services/search/client/favorites';
import { InvokeCommand } from '../../../results/models/invoke-command.model';
import { WidgetsService } from '../../services/widgets.service';
import { WidgetBaseComponent } from '../widget-base.component';
import { HomeTabsService } from 'src/app/bar/services/home-tabs.service';
import { AvatarListService } from 'src/app/bar/services/avatar-list.service';

@UntilDestroy()
@Component({
  selector: 'favorites-widget',
  templateUrl: './favorites-widget.component.html',
  styleUrls: ['./favorites-widget.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FavoritesWidgetComponent extends WidgetBaseComponent<HomeTabs.Widget> implements OnInit {
  constructor(
    protected elementRef: ElementRef,
    protected eventsService: EventsService,
    protected hubService: HubService,
    protected cdr: ChangeDetectorRef,
    protected keyboardService: KeyboardService,
    protected widgetsService: WidgetsService,
    protected resultCommandService: ResultCommandService,
    protected ngZone: NgZone,
    protected session: SessionService,
    private searchService: SearchService,
    private collectionsService: CollectionsService,
    protected contextMenuService: ContextMenuService,
    protected workspacesService: WorkspacesService,
    protected routerService: RouterService,
    protected popupService: PopupService,
    protected homeTabsService: HomeTabsService,
    protected avatarListService: AvatarListService
  ) {
    super(
      elementRef,
      cdr,
      keyboardService,
      widgetsService,
      ngZone,
      session,
      contextMenuService,
      workspacesService,
      popupService,
      eventsService,
      routerService,
      homeTabsService,
      avatarListService
    );
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.pageType = 'favorites';
  }

  async loadData() {
    this.isLoading = true;
    this.searchSession = this.searchService.getOrCreateSearchSession('favorites-widget');
    const favoritesSettings: FavoritesSourceSettings = {
      type: 'favorites',
      sources: {
        goLinks: { type: 'go-links', useMemSearch: true, withVisits: false, filters: { postFilters: { favorite: ['true'] } } },
        collections: { type: 'collection', filters: { postFilters: { favorite: ['true'] } }, isFavoriteItem: true },
        resources: { type: 'resources-favorites', cache: 'first' },
        staticCollection: {
          type: 'static-collection-items',
          filters: { postFilters: { favorite: ['true'] } },
          convertItem: true,
          isFavoriteItem: true,
        },
      },
      sorting: this.currentSort,
    };
    const options: SearchOptions = {
      sources: [favoritesSettings],
    };
    this.searchSession
      .search$(options)
      .pipe(untilDestroyed(this))
      .subscribe(async (ctx) => {
        if (ctx.searchCompleted) {
          this.displayedContext.items = ctx.items;
          this.isLoading = false;
          await this.onResizeChange();
          this.cdr.markForCheck();
        }
      });
  }

  public async onInvoke(data: InvokeCommand, type: Search.ResultType): Promise<void> {
    const eventInfo = { ...data.eventInfo, resources: [{ list: 'favorite' }] };
    data = { ...data, eventInfo };
    this.resultCommandService.onInvoke(
      this.displayedContext.items,
      data,
      type,
      'favorites',
      'favorites',
      'favorites',
      this.selectedIndex,
      0
    );
  }

  handleRowMenuKeys(keys: Array<KeyName>, event: CustomKeyboardEvent): void {
    const selectedItem = this.displayedContext.items[this.selectedIndex];
    if (isEnterKey(keys[0]) && selectedItem?.type === 'collection') {
      this.onCollectionItemClick(<Collections.Collection>selectedItem);
    } else {
      super.handleRowMenuKeys(keys, event);
    }
  }

  onCollectionItemClick(item: Collections.Collection): void {
    this.collectionsService.openCollection(item, 'search-view');
  }
}
